// Generic time-axis line chart for {x: bucket epoch ms, y} datasets. `fill: true` renders
// the themed cyan area line; other datasets are auto-colored by the global theming plugin.
import { Chart } from 'chart.js'

import { chartPalette, verticalGradient } from './palette'
import { dragZoomPlugin } from './drag_zoom_plugin'
import { fillGaps, markNoData, noDataPlugin, timeXScaleOptions } from './time_axis'

export function buildTimeSeriesLine(canvas, optionsFromParam, { plugins = [] } = {}) {
    const {
        datasets: sourceDatasets = [],
        timeScale = {},
        noDataMessage,
    } = optionsFromParam.data || {}
    const palette = chartPalette()

    const noData = markNoData(canvas, sourceDatasets)

    const intervalMs = (timeScale.intervalSeconds || 0) * 1000

    // Chart.js 4's time-scale layout crashes outright on zero points, so empty renders axes only.
    const datasets = noData
        ? []
        : sourceDatasets.map((dataset) => ({
              type: 'line',
              label: dataset.label,
              data: fillGaps(dataset.data, intervalMs, timeScale.windowStart, timeScale.windowEnd),
              tension: 0.35,
              pointRadius: 0,
              pointHoverRadius: 4,
              borderJoinStyle: 'round',
              ...(dataset.fill
                  ? {
                        fill: true,
                        borderColor: palette.callsLine,
                        backgroundColor: verticalGradient([
                            [0, palette.areaFillTop],
                            [1, palette.areaFillBottom],
                        ]),
                        pointBackgroundColor: palette.callsLine,
                        pointBorderColor: '#fff',
                        pointBorderWidth: 1.5,
                    }
                  : {}),
          }))

    return new Chart(canvas, {
        plugins: [
            ...plugins,
            dragZoomPlugin(intervalMs, palette.callsLine),
            ...(noData
                ? [
                      noDataPlugin(
                          noDataMessage || 'No data for the selected time frame',
                          palette.ink,
                      ),
                  ]
                : []),
        ],
        data: { datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            resizeDelay: 0,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (item) => {
                            const value = Number(item.parsed.y).toLocaleString()
                            return `${item.dataset.label}: ${value}`
                        },
                    },
                },
            },
            scales: {
                // Lines sit on the bucket instants, so no band offset.
                x: timeXScaleOptions(timeScale, { offset: false }),
                y: {
                    beginAtZero: true,
                    grid: { drawTicks: false },
                    border: { display: false },
                    // Counts are integers; precision matters for the empty chart, whose
                    // default 0–1 scale would otherwise tick at 0.2 steps.
                    ticks: { padding: 8, precision: 0 },
                },
            },
            animation: { duration: 320, easing: 'easeOutCubic' },
        },
    })
}
